import { useEffect, useRef, useState } from 'react';
import { DS2 } from '../../../theme/designTokens';
import { useAuth } from '../../../context/AuthContext';
import { useFriends } from '../../../context/FriendContext';
import { useChatDock, MAX_VISIBLE_TABS } from '../../../context/ChatDockContext';
import { PlayerAvatar } from '../../ui/PlayerAvatar';
import { UnreadBadge } from '../../ui/UnreadBadge';
import { ChatDockWindow } from './ChatDockWindow';
import { useTranslation } from '../../../i18n/react';

function RestoreIcon() {
  return (
    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 15l6-6 6 6" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TabCloseIcon() {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 5l14 14M19 5L5 19" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

interface TabProps {
  friendId: number;
  tabRef: (el: HTMLButtonElement | null) => void;
}

/** A minimized conversation: narrow persistent tab with identity, unread
 *  badge, restore (the tab itself) and a distinct close control. */
function ChatMinimizedTab({ friendId, tabRef }: TabProps) {
  const { t } = useTranslation('messages');
  const { conversations, friends } = useFriends();
  const { restoreConversation, closeConversation } = useChatDock();

  const conversation = conversations.find(c => c.friend.id === friendId);
  const friendInfo = conversation?.friend ?? friends.find(f => f.friend.id === friendId)?.friend ?? null;
  const name = friendInfo?.display_name ?? '';
  const unread = conversation?.unread_count ?? 0;

  return (
    <div
      role="group"
      aria-label={t('chat.tabAria', { name })}
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        background: '#101010', border: '1px solid rgba(201,168,76,0.4)',
        borderRadius: 10, padding: '4px 6px 4px 4px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
      }}
    >
      <button
        type="button"
        ref={tabRef}
        aria-label={`${t('chat.restore')} — ${name}`}
        title={t('chat.restore')}
        onClick={() => restoreConversation(friendId)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, maxWidth: 150,
          background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 2px',
        }}
      >
        <PlayerAvatar avatarKey={friendInfo?.avatar_key} name={name} size={22} />
        <span
          style={{
            fontFamily: DS2.font.body, fontSize: 12, fontWeight: unread > 0 ? 700 : 500,
            color: '#FFFFFF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {name}
        </span>
        {unread > 0 && (
          <span aria-label={t('chat.unreadAria', { count: unread })}>
            <UnreadBadge count={unread} />
          </span>
        )}
        <RestoreIcon />
      </button>
      <button
        type="button"
        aria-label={`${t('chat.close')} — ${name}`}
        title={t('chat.close')}
        onClick={() => closeConversation(friendId)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: 6, border: 'none',
          background: 'transparent', cursor: 'pointer', flexShrink: 0,
        }}
      >
        <TabCloseIcon />
      </button>
    </div>
  );
}

/**
 * Shell-level dock layer: expanded conversation windows docked to the
 * lower-right, minimized tabs, the deterministic overflow control, focus
 * restoration and ONE polite live-announcement region for incoming visible
 * DM content. Rendered once by AppShell — route navigation re-renders but
 * never remounts it, so windows/tabs/drafts survive navigation intact.
 *
 * On suppressed routes (game/spectate/tutorial) the whole layer is hidden
 * with CSS while STAYING MOUNTED: no window state, draft, scroll position or
 * cache is touched, and the exact prior presentation returns when
 * suppression ends.
 */
export function ChatDockLayer() {
  const { t } = useTranslation('messages');
  const { isLoggedIn } = useAuth();
  // Defensive defaults mirror ChatSidebarModule (partial mocks in old tests).
  const {
    threads: threadsRaw,
    conversations: conversationsRaw,
    friends: friendsRaw,
    lastReceivedMessageIds: lastReceivedRaw,
  } = useFriends();
  const threads = threadsRaw ?? {};
  const conversations = conversationsRaw ?? [];
  const friends = friendsRaw ?? [];
  const lastReceivedMessageIds = lastReceivedRaw ?? {};
  const {
    openIds, expandedIds, minimizedIds, lastEvent,
    isDesktopDock, suppressed, documentVisible, getModuleHeader,
  } = useChatDock();

  const [overflowOpen, setOverflowOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const windowRefs = useRef(new Map<number, HTMLDivElement>());
  const tabRefs = useRef(new Map<number, HTMLButtonElement>());
  const overflowButtonRef = useRef<HTMLButtonElement>(null);
  const overflowPopoverRef = useRef<HTMLDivElement>(null);
  const prevReceivedRef = useRef<Record<number, number>>({});

  const visibleTabs = minimizedIds.slice(0, MAX_VISIBLE_TABS);
  const overflowTabs = minimizedIds.slice(MAX_VISIBLE_TABS);

  // ── Focus restoration for user-initiated transitions ──────────────────────
  useEffect(() => {
    if (!lastEvent) return;
    const { kind, id } = lastEvent;
    if (kind === 'minimized') {
      // Focus returns to the minimized tab (or the overflow control when the
      // tab collapsed into it).
      (tabRefs.current.get(id) ?? overflowButtonRef.current)?.focus();
    } else if (kind === 'closed') {
      getModuleHeader()?.focus();
    } else {
      windowRefs.current.get(id)?.focus();
    }
  }, [lastEvent, getModuleHeader]);

  // ── Close the overflow menu when it empties ───────────────────────────────
  useEffect(() => {
    if (overflowTabs.length === 0) setOverflowOpen(false);
  }, [overflowTabs.length]);

  useEffect(() => {
    function onOutside(e: MouseEvent) {
      if (
        overflowPopoverRef.current && !overflowPopoverRef.current.contains(e.target as Node) &&
        overflowButtonRef.current && !overflowButtonRef.current.contains(e.target as Node)
      ) setOverflowOpen(false);
    }
    if (overflowOpen) document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [overflowOpen]);

  // ── ONE polite live announcement per incoming message id, only while its
  //    conversation is visibly presented (expanded, unsuppressed, document
  //    visible). FriendContext already deduplicates REST/cable delivery, and
  //    lastReceivedMessageIds only ever advances — so a redelivered message
  //    can never announce twice. ───────────────────────────────────────────
  useEffect(() => {
    const prev = prevReceivedRef.current;
    prevReceivedRef.current = lastReceivedMessageIds;
    if (suppressed || !documentVisible) return;
    for (const key of Object.keys(lastReceivedMessageIds)) {
      const friendId = Number(key);
      const messageId = lastReceivedMessageIds[friendId];
      if ((prev[friendId] ?? 0) >= messageId) continue;
      if (!expandedIds.includes(friendId)) continue;
      const from = conversations.find(c => c.friend.id === friendId)?.friend
        ?? friends.find(f => f.friend.id === friendId)?.friend;
      if (!from) continue;
      const message = (threads[friendId] ?? []).find(m => m.id === messageId);
      const body = message?.body ?? '';
      const raw = body.length > 60 ? `${body.slice(0, 60)}…` : body;
      const text = raw || (message?.image_url ? t('previewPhoto') : '');
      setAnnouncement(t('toast.incoming', { name: from.display_name, text }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastReceivedMessageIds]);

  if (!isLoggedIn || !isDesktopDock || openIds.length === 0) return null;

  return (
    <div
      data-testid="chat-dock-layer"
      style={{
        position:  'fixed',
        bottom:    0,
        right:     16,
        zIndex:    45,
        display:   suppressed ? 'none' : 'flex',
        alignItems: 'flex-end',
        gap:       10,
      }}
    >
      {/* Expanded windows (minimized ones stay mounted and hidden so their
          drafts, scroll and history survive minimize/restore). */}
      {openIds.map(id => (
        <ChatDockWindow
          key={id}
          friendId={id}
          minimized={!expandedIds.includes(id)}
          rootRef={el => {
            if (el) windowRefs.current.set(id, el);
            else windowRefs.current.delete(id);
          }}
        />
      ))}

      {/* Minimized tabs + deterministic overflow */}
      {minimizedIds.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, paddingBottom: 8 }}>
          {overflowTabs.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                ref={overflowButtonRef}
                aria-label={t('chat.overflowAria')}
                aria-expanded={overflowOpen}
                onClick={() => setOverflowOpen(o => !o)}
                style={{
                  background: '#101010', border: '1px solid rgba(201,168,76,0.4)',
                  borderRadius: 10, padding: '5px 10px', color: DS2.color.gold,
                  fontFamily: DS2.font.body, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
                }}
              >
                {t('chat.overflow', { count: overflowTabs.length })}
              </button>
              {overflowOpen && (
                <div
                  ref={overflowPopoverRef}
                  style={{
                    position: 'absolute', bottom: 'calc(100% + 6px)', right: 0,
                    display: 'flex', flexDirection: 'column', gap: 4, padding: 6,
                    background: '#161616', border: '1px solid rgba(201,168,76,0.3)',
                    borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                  }}
                >
                  {overflowTabs.map(id => (
                    <ChatMinimizedTab
                      key={id}
                      friendId={id}
                      tabRef={el => {
                        if (el) tabRefs.current.set(id, el);
                        else tabRefs.current.delete(id);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {visibleTabs.map(id => (
            <ChatMinimizedTab
              key={id}
              friendId={id}
              tabRef={el => {
                if (el) tabRefs.current.set(id, el);
                else tabRefs.current.delete(id);
              }}
            />
          ))}
        </div>
      )}

      {/* One polite live region for incoming visible DM content. */}
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
          overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
        }}
      >
        {announcement}
      </div>
    </div>
  );
}
